import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  ChevronLeft,
  Loader2,
  RefreshCw,
  Activity,
  Users,
  BarChart3,
  Globe,
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminTraffic = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchData = async (windowDays = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/traffic?days=${windowDays}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load traffic stats");
      toast.error("Failed to load traffic stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchData(days);
  }, [isAdmin, days]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login?returnTo=/admin/traffic" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const maxDaily = data?.daily?.reduce((m, d) => Math.max(m, d.views), 0) || 1;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/" />
            <span className="text-text-muted">/</span>
            <Link to="/admin" className="text-text-muted hover:text-white text-sm">Admin</Link>
            <span className="text-text-muted">/</span>
            <span className="text-white text-sm font-medium">Traffic</span>
          </div>
          <Link to="/admin">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <Activity className="w-7 h-7 text-[#D4F34A]" />
                Traffic
              </h1>
              <p className="text-text-muted text-sm mt-1">
                First-party analytics — public pages only, bots excluded
              </p>
            </div>
            <div className="flex items-center gap-2">
              {[7, 14, 30].map((d) => (
                <Button
                  key={d}
                  onClick={() => setDays(d)}
                  variant={days === d ? "default" : "outline"}
                  size="sm"
                  className={
                    days === d
                      ? "bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
                      : "border-[#262626] text-white hover:bg-white/5"
                  }
                  data-testid={`traffic-window-${d}`}
                >
                  {d}d
                </Button>
              ))}
              <Button
                onClick={() => fetchData(days)}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-[#262626] text-white hover:bg-white/5 ml-2"
                data-testid="traffic-refresh"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {error && (
            <Card className="bg-red-500/10 border-red-500/30 mb-6">
              <CardContent className="py-4">
                <p className="text-red-400">{error}</p>
              </CardContent>
            </Card>
          )}

          {loading && !data && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
            </div>
          )}

          {data && (
            <>
              {/* KPI tiles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-sm text-text-muted">Page Views</span>
                    </div>
                    <p className="text-3xl font-bold text-white" data-testid="traffic-total-views">
                      {data.total_views.toLocaleString()}
                    </p>
                    <p className="text-xs text-text-muted mt-1">Last {data.window_days} days</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-sm text-text-muted">Unique Visitors</span>
                    </div>
                    <p className="text-3xl font-bold text-white" data-testid="traffic-unique-visitors">
                      {data.unique_visitors.toLocaleString()}
                    </p>
                    <p className="text-xs text-text-muted mt-1">Distinct visitor IDs</p>
                  </CardContent>
                </Card>
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-sm text-text-muted">Views / Visitor</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {data.unique_visitors > 0
                        ? (data.total_views / data.unique_visitors).toFixed(1)
                        : "—"}
                    </p>
                    <p className="text-xs text-text-muted mt-1">Avg session depth</p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily trend bars */}
              <Card className="bg-surface border-[#262626] mb-6">
                <CardContent className="py-5">
                  <h3 className="text-sm font-semibold text-text-muted mb-4">
                    Daily views (last {data.window_days} days)
                  </h3>
                  <div className="flex items-end gap-2 h-40" data-testid="traffic-daily-chart">
                    {data.daily.map((d) => {
                      const heightPct = (d.views / maxDaily) * 100;
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full flex flex-col items-center justify-end h-full">
                            <span className="text-xs text-text-muted mb-1">{d.views}</span>
                            <div
                              className="w-full bg-[#D4F34A]/80 rounded-t"
                              style={{ height: `${Math.max(heightPct, 2)}%`, minHeight: "2px" }}
                              title={`${d.views} views, ${d.unique_visitors} visitors`}
                            />
                          </div>
                          <span className="text-[10px] text-text-muted">
                            {d.date.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top pages + referrers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#D4F34A]" /> Top pages
                    </h3>
                    {data.top_paths.length === 0 ? (
                      <p className="text-text-muted text-sm">No data yet.</p>
                    ) : (
                      <div className="space-y-2" data-testid="traffic-top-paths">
                        {data.top_paths.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-white truncate flex-1 mr-3">{p.path}</span>
                            <span className="text-text-muted tabular-nums">{p.views}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-surface border-[#262626]">
                  <CardContent className="py-5">
                    <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#D4F34A]" /> Top referrers
                    </h3>
                    {data.top_referrers.length === 0 ? (
                      <p className="text-text-muted text-sm">No external referrers yet.</p>
                    ) : (
                      <div className="space-y-2" data-testid="traffic-top-referrers">
                        {data.top_referrers.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-white truncate flex-1 mr-3">{r.referrer}</span>
                            <span className="text-text-muted tabular-nums">{r.views}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminTraffic;
